<template>
  <div class="orangehrm-background-container company-days">
    <section
      class="orangehrm-card-container"
      aria-labelledby="company-days-title"
    >
      <div class="company-days-heading">
        <oxd-text id="company-days-title" tag="h6" class="orangehrm-main-title">
          Company Days Off
        </oxd-text>
        <oxd-button
          type="button"
          display-type="ghost"
          label="Refresh status"
          :disabled="busy"
          @click="refresh"
        />
      </div>
      <oxd-text tag="p" class="company-days-description">
        Give staff an extra day off to recognize their work. Save a draft,
        review who is affected, then publish it to staff calendars.
      </oxd-text>
      <oxd-text tag="p" class="company-days-description">
        {{ audienceDescription }} These full days do not deduct vacation or sick
        time. Connected Google Calendars receive an Out of Office block that
        declines new invitations only. Existing meetings stay unchanged.
      </oxd-text>
      <oxd-text v-if="loading" tag="p" role="status"
        >Loading company days off…</oxd-text
      >
      <oxd-text v-if="error" tag="p" class="company-days-error" role="alert">
        {{ error }}
      </oxd-text>
      <oxd-text
        v-if="message"
        tag="p"
        class="company-days-notice"
        role="status"
      >
        {{ message }}
      </oxd-text>
      <oxd-text
        v-if="loaded && !calendarEnabled"
        tag="p"
        class="company-days-warning"
        role="status"
      >
        Calendar delivery is disabled in this environment. You can save and
        review drafts here.
      </oxd-text>
      <template v-if="loaded">
        <oxd-divider />
        <oxd-text tag="h6">{{
          editingId ? 'Edit draft' : 'Add a company day off'
        }}</oxd-text>
        <oxd-form @submit-valid="saveDraft">
          <div class="company-days-fields">
            <oxd-input-field
              v-model="form.name"
              label="Day name"
              placeholder="For example, Staff Appreciation Day"
              :maxlength="200"
              :disabled="busy"
              required
            />
            <date-input
              v-model="form.date"
              label="Date"
              :disabled="busy"
              required
            />
          </div>
          <oxd-text
            v-if="formTouched && validationError"
            tag="p"
            class="company-days-error"
            role="alert"
          >
            {{ validationError }}
          </oxd-text>
          <div class="company-days-actions">
            <oxd-button
              type="submit"
              display-type="secondary"
              :label="saving ? 'Saving…' : 'Save draft and review'"
              :disabled="busy"
            />
            <oxd-button
              v-if="editingId"
              type="button"
              display-type="ghost"
              label="Stop editing"
              :disabled="busy"
              @click="resetForm"
            />
            <oxd-text tag="p"
              >Saving a draft does not change anyone’s calendar.</oxd-text
            >
          </div>
        </oxd-form>
      </template>
    </section>

    <section
      v-if="loaded"
      class="orangehrm-card-container company-days-section"
      aria-labelledby="company-days-list-title"
    >
      <oxd-text id="company-days-list-title" tag="h6"
        >Scheduled and draft days</oxd-text
      >
      <oxd-text v-if="!days.length" tag="p" class="company-days-description">
        No company days off have been added yet.
      </oxd-text>
      <div
        v-else
        class="company-days-table-scroll"
        tabindex="0"
        aria-label="Company days off"
      >
        <table class="company-days-table">
          <caption>
            Review a day for its audience and calendar delivery details.
          </caption>
          <thead>
            <tr>
              <th scope="col">Day</th>
              <th scope="col">Date</th>
              <th scope="col">Status</th>
              <th scope="col">Calendars</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="day in days" :key="day.id">
              <th scope="row">{{ day.name }}</th>
              <td>{{ displayDate(day.date) }}</td>
              <td>{{ statusLabel(day.status) }}</td>
              <td>{{ calendarSummary(day) }}</td>
              <td>
                <oxd-button
                  type="button"
                  display-type="ghost"
                  label="Review"
                  :aria-label="`Review ${day.name}`"
                  :disabled="busy"
                  @click="review(day.id)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section
      v-if="selected || loadingDetail"
      ref="reviewPanel"
      tabindex="-1"
      class="orangehrm-card-container company-days-section"
      aria-labelledby="company-days-review-title"
    >
      <oxd-text id="company-days-review-title" tag="h6"
        >Review company day off</oxd-text
      >
      <oxd-text v-if="loadingDetail" tag="p" role="status"
        >Loading the latest details…</oxd-text
      >
      <template v-else-if="selected">
        <dl class="company-days-details">
          <div>
            <dt>Day</dt>
            <dd>{{ selected.name }}</dd>
          </div>
          <div>
            <dt>Date</dt>
            <dd>{{ displayDate(selected.date) }} · Full day</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>{{ statusLabel(selected.status) }}</dd>
          </div>
          <div>
            <dt>Staff included</dt>
            <dd>{{ audience.total }}</dd>
          </div>
          <div>
            <dt>Connected calendars</dt>
            <dd>{{ audience.connected }}</dd>
          </div>
          <div>
            <dt>Not connected</dt>
            <dd>{{ audience.notConnected }}</dd>
          </div>
        </dl>
        <oxd-text
          v-if="audience.notConnected"
          tag="p"
          class="company-days-warning"
        >
          {{ audience.notConnected }} staff members have not connected a
          calendar. They still receive the day off in OrangeHRM. Calendar blocks
          can be added after they connect.
        </oxd-text>
        <oxd-text
          v-if="selected.status === 'draft'"
          tag="p"
          class="company-days-description"
        >
          Review this saved draft before publishing. The title and date cannot
          be edited after publication.
        </oxd-text>
        <oxd-text v-else tag="p" class="company-days-description">
          {{ calendarSummary(selected) }}. Calendar changes can take up to five
          minutes. Select Refresh status to check progress.
        </oxd-text>
        <div v-if="conflicts.length" class="company-days-warning" role="status">
          <oxd-text tag="p">
            Existing leave overlaps this date. Review these requests before
            {{ selected.status === 'draft' ? 'publishing' : 'cancelling' }} the
            company day off so staff balances remain correct.
          </oxd-text>
          <ul>
            <li
              v-for="(conflict, index) in conflicts"
              :key="`${conflict.requestId}-${index}`"
            >
              {{ conflict.employeeName }} — request #{{ conflict.requestId }},
              {{ displayDate(conflict.date) }} ({{
                leaveStatus(conflict.status)
              }})
            </li>
          </ul>
        </div>
        <div
          v-if="selected.blockingReasons && selected.blockingReasons.length"
          class="company-days-warning"
          role="status"
        >
          <ul>
            <li v-for="reason in selected.blockingReasons" :key="reason">
              {{ reason }}
            </li>
          </ul>
        </div>
        <div
          v-if="failedDeliveries.length"
          class="company-days-warning"
          role="status"
        >
          <oxd-text tag="p">These calendar updates need attention:</oxd-text>
          <ul>
            <li v-for="(delivery, index) in failedDeliveries" :key="index">
              {{ delivery.employeeName }} —
              {{ delivery.lastError || 'Calendar update failed.' }}
            </li>
          </ul>
        </div>
        <details
          v-if="selected.deliveries && selected.deliveries.length"
          class="company-days-deliveries"
        >
          <summary>
            Calendar details for {{ selected.deliveries.length }} staff members
          </summary>
          <ul>
            <li v-for="(delivery, index) in selected.deliveries" :key="index">
              {{ delivery.employeeName }} —
              {{ deliveryStatus(delivery.status) }}
            </li>
          </ul>
        </details>
        <oxd-text
          v-if="editingId === selected.id"
          tag="p"
          class="company-days-warning"
        >
          Finish editing and save the draft before publishing.
        </oxd-text>
        <div class="company-days-actions">
          <oxd-button
            v-if="selected.status === 'draft'"
            type="button"
            display-type="ghost"
            label="Edit draft"
            :disabled="busy || Boolean(editingId)"
            @click="editDraft"
          />
          <oxd-button
            v-if="selected.status === 'draft'"
            type="button"
            display-type="secondary"
            label="Publish to staff calendars"
            :disabled="!canPublish"
            @click="confirmAction('publish')"
          />
          <oxd-button
            v-if="selected.canCancel"
            type="button"
            display-type="ghost"
            :label="
              selected.status === 'draft' ? 'Discard draft' : 'Cancel day off'
            "
            :disabled="busy || Boolean(editingId)"
            @click="confirmAction('cancel')"
          />
          <oxd-button
            v-if="selected.calendar && selected.calendar.failed > 0"
            type="button"
            display-type="secondary"
            label="Retry failed calendar updates"
            :disabled="busy || !calendarEnabled"
            @click="performAction('retry', selected)"
          />
        </div>
      </template>
    </section>

    <teleport to="#app">
      <oxd-dialog
        v-if="confirmation"
        class="company-days-dialog"
        :style="{width: '560px', maxWidth: '95vw'}"
        role="dialog"
        aria-modal="true"
        aria-labelledby="company-day-confirm-title"
        @update:show="closeConfirmation"
      >
        <oxd-text id="company-day-confirm-title" tag="h6">
          {{
            confirmation.action === 'publish'
              ? 'Publish company day off?'
              : 'Cancel company day off?'
          }}
        </oxd-text>
        <oxd-text tag="p" class="company-days-description">
          {{ confirmation.name }} · {{ displayDate(confirmation.date) }}
        </oxd-text>
        <oxd-text
          v-if="confirmation.action === 'publish'"
          tag="p"
          class="company-days-description"
        >
          This gives {{ audience.total }} staff members the full day off and
          adds Out of Office blocks to connected calendars. Only new conflicting
          invitations will be declined. No vacation or sick time will be
          deducted.
        </oxd-text>
        <oxd-text
          v-else-if="confirmation.status === 'draft'"
          tag="p"
          class="company-days-description"
        >
          Discard this unpublished draft. No calendars will be changed.
        </oxd-text>
        <oxd-text v-else tag="p" class="company-days-description">
          This removes the company day off and its calendar blocks. Meeting
          declines already sent cannot be reversed. Existing leave requests may
          need to be resolved before cancellation is possible.
        </oxd-text>
        <oxd-text
          v-if="actionError"
          tag="p"
          class="company-days-error"
          role="alert"
        >
          {{ actionError }}
        </oxd-text>
        <div class="company-days-actions">
          <oxd-button
            type="button"
            display-type="ghost"
            label="Go back"
            :disabled="acting"
            @click="closeConfirmation"
          />
          <oxd-button
            type="button"
            display-type="secondary"
            :label="
              acting
                ? 'Saving…'
                : confirmation.action === 'publish'
                ? 'Confirm publication'
                : 'Confirm cancellation'
            "
            :disabled="acting"
            @click="performAction(confirmation.action, confirmation)"
          />
        </div>
      </oxd-dialog>
    </teleport>
  </div>
</template>

<script>
import {OxdDialog} from '@ohrm/oxd';
import {APIService} from '@/core/util/services/api.service';

const endpoint = '/api/v2/leave/company-days-off';

export default {
  components: {'oxd-dialog': OxdDialog},
  setup() {
    const http = new APIService(window.appGlobal.baseUrl, endpoint);
    http.setIgnorePath(endpoint);
    return {http};
  },
  data() {
    return {
      days: [],
      today: '',
      loaded: false,
      loading: false,
      loadingDetail: false,
      saving: false,
      acting: false,
      calendarEnabled: false,
      audienceDescription: '',
      form: {name: '', date: ''},
      formTouched: false,
      editingId: null,
      editingVersion: null,
      selected: null,
      audience: {total: 0, connected: 0, notConnected: 0},
      conflicts: [],
      confirmation: null,
      error: '',
      actionError: '',
      message: '',
    };
  },
  computed: {
    busy() {
      return (
        this.loading ||
        this.loadingDetail ||
        this.saving ||
        this.acting ||
        Boolean(this.confirmation)
      );
    },
    validationError() {
      if (!this.form.name.trim()) return 'Enter a name for the day off.';
      if (this.form.name.trim().length > 200)
        return 'Keep the name to 200 characters or fewer.';
      if (!/^\d{4}-\d{2}-\d{2}$/.test(this.form.date))
        return 'Enter a valid date.';
      const date = new Date(`${this.form.date}T12:00:00Z`);
      if (
        Number.isNaN(date.getTime()) ||
        date.toISOString().slice(0, 10) !== this.form.date
      )
        return 'Enter a valid date.';
      if (!this.today || this.form.date < this.today)
        return 'Choose today or a future date.';
      return '';
    },
    canPublish() {
      return (
        !this.busy &&
        !this.editingId &&
        this.calendarEnabled &&
        this.selected?.status === 'draft' &&
        this.selected.canPublish === true &&
        !this.conflicts.length
      );
    },
    failedDeliveries() {
      return (this.selected?.deliveries || []).filter(
        (delivery) => delivery.status === 'failed',
      );
    },
  },
  beforeMount() {
    this.loadDays();
  },
  methods: {
    errorMessage(error, fallback) {
      const message =
        error?.response?.data?.error?.message || error?.data?.error?.message;
      return typeof message === 'string' && message.trim() ? message : fallback;
    },
    async loadDays() {
      this.loading = true;
      this.error = '';
      try {
        const {data} = await this.http.getAll();
        this.days = data.data.days;
        this.today = data.data.today;
        this.calendarEnabled = data.data.calendarEnabled === true;
        this.audienceDescription = data.data.audienceDescription;
        this.loaded = true;
      } catch (error) {
        this.error = this.errorMessage(
          error,
          'Unable to load company days off. Select Refresh status to try again.',
        );
      } finally {
        this.loading = false;
      }
    },
    async refresh() {
      if (this.busy) return;
      const selectedId = this.selected?.id;
      await this.loadDays();
      if (selectedId && !this.error) await this.loadDetail(selectedId);
    },
    async review(id) {
      if (this.busy) return;
      this.message = '';
      this.error = '';
      await this.loadDetail(id);
      this.$nextTick(() => this.$refs.reviewPanel?.focus());
    },
    async loadDetail(id) {
      this.loadingDetail = true;
      this.selected = null;
      try {
        const {data} = await this.http.getAll({dayId: id});
        this.selected = data.data.day;
        this.audience = data.data.audience;
        this.conflicts = data.data.conflicts || data.data.day.conflicts || [];
      } catch (error) {
        this.error = this.errorMessage(
          error,
          'Unable to load this day. Select Review to try again.',
        );
      } finally {
        this.loadingDetail = false;
      }
    },
    resetForm() {
      this.form = {name: '', date: ''};
      this.editingId = null;
      this.editingVersion = null;
      this.formTouched = false;
    },
    editDraft() {
      if (this.busy || this.selected?.status !== 'draft') return;
      this.editingId = this.selected.id;
      this.editingVersion = this.selected.version;
      this.form = {name: this.selected.name, date: this.selected.date};
      this.formTouched = false;
      this.message = '';
    },
    async saveDraft() {
      if (this.busy || !this.loaded) return;
      this.formTouched = true;
      if (this.validationError) return;
      this.saving = true;
      this.error = '';
      this.message = '';
      try {
        const payload = {name: this.form.name.trim(), date: this.form.date};
        const response = this.editingId
          ? await this.http.request({
              method: 'PUT',
              data: {
                ...payload,
                id: this.editingId,
                version: this.editingVersion,
                action: 'update',
              },
            })
          : await this.http.create(payload);
        const day = response.data.data.day;
        this.resetForm();
        this.upsertDay(day);
        this.message =
          'Draft saved. Review the details below before publishing.';
        this.$toast?.saveSuccess();
        await this.loadDetail(day.id);
        this.$nextTick(() => this.$refs.reviewPanel?.focus());
      } catch (error) {
        this.error = this.errorMessage(
          error,
          'Unable to confirm the draft was saved. Your entries are retained. Refresh status before trying again.',
        );
      } finally {
        this.saving = false;
      }
    },
    upsertDay(day) {
      const index = this.days.findIndex((existing) => existing.id === day.id);
      if (index < 0) this.days.unshift(day);
      else this.days.splice(index, 1, day);
    },
    confirmAction(action) {
      if (this.busy || !this.selected || this.editingId) return;
      if (action === 'publish' && !this.canPublish) return;
      if (action === 'cancel' && !this.selected.canCancel) return;
      this.actionError = '';
      this.confirmation = {...this.selected, action};
    },
    closeConfirmation() {
      if (!this.acting) this.confirmation = null;
    },
    async performAction(action, day) {
      if (this.acting || this.saving || this.loadingDetail || this.loading)
        return;
      if (
        action === 'retry' &&
        (!this.calendarEnabled || !day.calendar?.failed)
      )
        return;
      if (
        action !== 'retry' &&
        (!this.confirmation ||
          this.confirmation.id !== day.id ||
          this.confirmation.action !== action)
      )
        return;
      this.acting = true;
      this.error = '';
      this.actionError = '';
      this.message = '';
      try {
        const {data} = await this.http.request({
          method: 'PUT',
          data: {id: day.id, version: day.version, action},
        });
        this.upsertDay(data.data.day);
        this.confirmation = null;
        this.message =
          action === 'publish'
            ? 'Company day off published. Calendar updates are queued; allow up to five minutes.'
            : action === 'cancel'
            ? day.status === 'draft'
              ? 'Draft discarded. No calendars were changed.'
              : this.calendarEnabled
              ? 'Company day off cancelled. Any calendar blocks are queued for removal.'
              : 'Company day off cancelled. Calendar delivery is disabled in this environment.'
            : 'Failed calendar updates are queued to try again.';
        this.$toast?.updateSuccess();
        await this.loadDetail(day.id);
      } catch (error) {
        const message = this.errorMessage(
          error,
          'Unable to confirm this change. Refresh status before trying again.',
        );
        if (this.confirmation) this.actionError = message;
        else this.error = message;
      } finally {
        this.acting = false;
      }
    },
    displayDate(value) {
      if (!value) return 'Unavailable';
      const date = new Date(`${value}T12:00:00Z`);
      if (Number.isNaN(date.getTime())) return value;
      return new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
    },
    statusLabel(status) {
      return (
        {draft: 'Draft', published: 'Published', cancelled: 'Cancelled'}[
          status
        ] || status
      );
    },
    leaveStatus(status) {
      return (
        {1: 'Pending approval', 2: 'Approved', 3: 'Taken', 5: 'Holiday'}[
          status
        ] || status
      );
    },
    deliveryStatus(status) {
      return (
        {
          pending: 'Pending',
          synced: 'Added to calendar',
          failed: 'Needs attention',
          removed: 'Removed',
          not_connected: 'Calendar not connected',
        }[status] || status
      );
    },
    calendarSummary(day) {
      if (day.status === 'draft') return 'Not published';
      const counts = day.calendar || {};
      const parts = [];
      if (counts.synced) parts.push(`${counts.synced} added`);
      if (counts.pending) parts.push(`${counts.pending} pending`);
      if (counts.failed) parts.push(`${counts.failed} need attention`);
      if (counts.removed) parts.push(`${counts.removed} removed`);
      if (counts.notConnected)
        parts.push(`${counts.notConnected} not connected`);
      return parts.join(' · ') || 'No calendar updates';
    },
  },
};
</script>

<style lang="scss" scoped>
.company-days {
  color: #38455c;
  &-section {
    margin-top: 1.25rem;
  }
  &-heading,
  &-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
  }
  &-heading {
    justify-content: space-between;
  }
  &-actions {
    margin-top: 1rem;
  }
  &-description {
    margin: 0.75rem 0;
    line-height: 1.6;
  }
  &-fields {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    gap: 1.25rem;
    max-width: 800px;
    margin-top: 1rem;
  }
  &-warning,
  &-notice,
  &-error {
    padding: 0.85rem 1rem;
    margin: 1rem 0;
    border-radius: 0.4rem;
    line-height: 1.6;
  }
  &-warning {
    background: #fff5dd;
    color: #614a13;
  }
  &-notice {
    background: #edf7f1;
    color: #235a3b;
  }
  &-error {
    background: #fff0f0;
    color: #9b2424;
  }
  &-table-scroll {
    overflow-x: auto;
  }
  &-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
    text-align: left;
  }
  &-table caption {
    text-align: left;
    padding: 0.9rem 0;
  }
  &-table th,
  &-table td {
    border-bottom: 1px solid #e8eaee;
    padding: 0.85rem 0.7rem;
    vertical-align: top;
  }
  &-table thead th {
    background: #f6f7f9;
  }
  &-details {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.25rem;
    margin: 1.25rem 0;
    font-size: 0.875rem;
  }
  &-details dt {
    font-weight: 600;
    margin-bottom: 0.35rem;
  }
  &-details dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
  &-deliveries {
    margin-top: 1rem;
    font-size: 0.875rem;
    line-height: 1.7;
  }
  &-deliveries summary {
    cursor: pointer;
  }
  ul {
    padding-left: 1.25rem;
  }
  @media (max-width: 640px) {
    &-fields,
    &-details {
      grid-template-columns: minmax(0, 1fr);
    }
    &-actions {
      align-items: stretch;
    }
  }
}
.company-days-dialog {
  padding: 1.5rem;
}
</style>
